import PubSub from "underpub-js";
import gsap from 'gsap';
import BasePopup from "../../../../src/scripts/classes/BasePopup";

/**
 * Unified Modal Component
 * 
 * Supports three modal types:
 * - slide: Slides in from right, ~50% width, right-aligned
 * - full: Full window takeover (100% width/height)
 * - center: Centered modal (~40% width/height)
 * 
 * Features:
 * - Only one modal can be open at a time (opening one closes others)
 * - Close via close button, overlay click, or ESC key
 * - Prevents body scroll when open
 * - Smooth GSAP animations for each type
 * 
 * Usage:
 * 1. Modals are automatically rendered via render-modals.twig partial
 * 2. Trigger a modal with: <a href="#modal-{entryId}">Open Modal</a>
 * 3. Modal IDs are generated as "modal-{craftEntryId}"
 * 
 * Example:
 *   <a href="#modal-123">Open Modal</a>
 * 
 * @class Modal
 */
var Modal = function(options) {

	this.options = {
		$el: false,
		baseClass: 'mol-Modal',
	};

	$.extend(true, this.options, options);

	// Get modal type from data attribute
	this.type = this.options.$el.attr('data-modal-type') || 'center';
	this.modalId = this.options.$el.attr('data-modal-id') || null;
	this.trigger = this.options.$el.attr('data-modal-trigger') || null;
	
	// Cache DOM elements
	this.$component = this.options.$el.find('.modal_component');
	this.$contentWrapper = this.options.$el.find('.modal_content-wrapper');
	this.$overlay = this.options.$el.find('.modal_background-overlay');
	this.$closeButton = this.options.$el.find('.modal_close-button');
	
	// State
	this.isOpen = false;
	this.isOpening = false; // Flag to prevent race conditions
	
	// GSAP timeline for animations
	this.timeline = null;

};

/**
 * Initialize the modal component
 */
Modal.prototype.init = function() {

	var self = this;

	// Set initial state based on type
	this.setInitialState();
	
	// Bind close button click
	this.$closeButton.on('click', function(e) {
		e.preventDefault();
		self.close();
	});
	
	// Bind overlay click (close on backdrop)
	this.$overlay.on('click', function(e) {
		// Only close if clicking directly on overlay, not on content
		if (e.target === self.$overlay[0]) {
			self.close();
		}
	});
	
	// Bind ESC key handler (will be managed globally)
	this.bindEscapeKey();
	
	// Register this modal instance globally for trigger handling
	if (!window.ModalInstances) {
		window.ModalInstances = {};
		// Initialize global modal state management on first modal
		Modal.initGlobalStateManagement();
	}
	if (this.modalId) {
		window.ModalInstances[this.modalId] = this;
	}
	// Also register by custom trigger if provided
	if (this.trigger) {
		// Remove the # if present for consistency
		var triggerKey = this.trigger.replace(/^#/, '');
		if (!window.ModalTriggers) {
			window.ModalTriggers = {};
		}
		window.ModalTriggers[this.trigger] = this;
		window.ModalTriggers[triggerKey] = this; // Also store without #
	}

};

/**
 * Set initial state based on modal type
 */
Modal.prototype.setInitialState = function() {

	var self = this;
	
	// Hide modal initially
	this.options.$el.css('display', 'none');
	
	// Clear any existing transforms
	gsap.set(this.$contentWrapper, {
		clearProps: 'all'
	});
	
	// Set initial transform/opacity based on type
	if (this.type === 'slide') {
		// Slide: positioned right, start off-screen to the right (x: 100% moves it right by its width)
		gsap.set(this.$contentWrapper, {
			xPercent: 100,
			opacity: 0
		});
		gsap.set(this.$overlay, {
			opacity: 0
		});
	} else if (this.type === 'full') {
		// Full: positioned top-left, start below viewport (y: 100% moves it down by its height)
		gsap.set(this.$contentWrapper, {
			yPercent: 100,
			opacity: 0
		});
		gsap.set(this.$overlay, {
			opacity: 0
		});
	} else if (this.type === 'center') {
		// Center: centered, start scaled down and transparent
		// Use xPercent and yPercent to maintain centering while scaling
		gsap.set(this.$contentWrapper, {
			opacity: 0,
			scale: 0.8,
			xPercent: -50,
			yPercent: -50,
			transformOrigin: 'center center'
		});
		gsap.set(this.$overlay, {
			opacity: 0
		});
	}

};

/**
 * Open the modal
 */
Modal.prototype.open = function() {

	var self = this;
	
	// If this modal is already open or currently opening, don't do anything
	if (this.isOpen || this.isOpening) {
		return;
	}

	// Peel availability/hotspots mobile shell before stacking `.mol-Modal` on top.
	// Document handlers + bubbling order can race; this is synchronous and reliable.
	BasePopup.dismissActiveMapPopupModal();

	// Set opening flag to prevent race conditions
	this.isOpening = true;
	
	// Close any currently open modal first
	if (window.ModalInstances) {
		Object.keys(window.ModalInstances).forEach(function(modalId) {
			var modal = window.ModalInstances[modalId];
			if (modal && modal.isOpen && modal !== self) {
				modal.close();
			}
		});
	}
	
	// Disable body scrolling FIRST to prevent layout shift
	PubSub.publish('Sitewide:disableScrolling');
	
	// Use requestAnimationFrame to ensure scrollbar padding is applied before showing modal
	requestAnimationFrame(function() {
		// Show the modal element
		self.options.$el.css('display', 'block');
		
		// Animate based on type
		if (self.type === 'slide') {
			self.openSlide();
		} else if (self.type === 'full') {
			self.openFull();
		} else if (self.type === 'center') {
			self.openCenter();
		}
	});
	
	this.isOpen = true;
	this.isOpening = false; // Clear opening flag now that modal is open
	
	// Publish event
	PubSub.publish('Modal:opened', {
		modalId: this.modalId,
		type: this.type
	});

};

/**
 * Close the modal
 */
Modal.prototype.close = function() {

	var self = this;
	
	if (!this.isOpen) {
		return;
	}
	
	this.isOpen = false;
	this.isOpening = false; // Clear opening flag when closing
	
	// IMPORTANT: Call enableScrolling immediately when modal closes
	// Don't wait for animation - this ensures the counter is decremented right away
	// The animation callback will be a no-op to prevent double-calling
	PubSub.publish('Sitewide:enableScrolling');
	
	// Store a flag to track if scrolling has been re-enabled (for animation callback)
	var scrollingReEnabled = true; // Already called above
	var safetyTimeout = null;
	
	var enableScrollingSafely = function() {
		// This is now a no-op since we already called enableScrolling above
		// But we keep it to prevent errors if somehow this gets called
		if (!scrollingReEnabled) {
			scrollingReEnabled = true;
			if (safetyTimeout) {
				clearTimeout(safetyTimeout);
				safetyTimeout = null;
			}
		}
	};
	
	// Safety fallback: ensure scrolling is re-enabled after max animation duration (600ms)
	// This is now just a safety net, but shouldn't be needed since we call it immediately above
	safetyTimeout = setTimeout(enableScrollingSafely, 600);
	
	// Animate out based on type
	// Note: enableScrolling will be called in the animation's onComplete callback
	if (this.type === 'slide') {
		this.closeSlide(enableScrollingSafely);
	} else if (this.type === 'full') {
		this.closeFull(enableScrollingSafely);
	} else if (this.type === 'center') {
		this.closeCenter(enableScrollingSafely);
	}
	
	// Publish event
	PubSub.publish('Modal:closed', {
		modalId: this.modalId,
		type: this.type
	});

};

/**
 * Open animation for slide type
 */
Modal.prototype.openSlide = function() {

	var self = this;
	
	// Kill any existing timeline
	if (this.timeline) {
		this.timeline.kill();
	}
	
	this.timeline = gsap.timeline({
		onComplete: function() {
			self.timeline = null;
		}
	});
	
	// Animate overlay fade in
	this.timeline.to(this.$overlay, {
		opacity: 1,
		duration: 0.3,
		ease: 'power2.out'
	}, 0);
	
	// Animate content slide in from right (xPercent: 0 brings it to its CSS position)
	this.timeline.to(this.$contentWrapper, {
		xPercent: 0,
		opacity: 1,
		duration: 0.4,
		ease: 'power3.out'
	}, 0.1);

};

/**
 * Close animation for slide type
 */
Modal.prototype.closeSlide = function(onCompleteCallback) {

	var self = this;
	
	// Kill any existing timeline
	if (this.timeline) {
		this.timeline.kill();
	}
	
	this.timeline = gsap.timeline({
		onComplete: function() {
			self.options.$el.css('display', 'none');
			self.setInitialState();
			self.timeline = null;
			// Animation complete - callback is a no-op since we already called enableScrolling immediately
			if (onCompleteCallback) {
				onCompleteCallback();
			}
		}
	});
	
	// Animate content slide out to right
	this.timeline.to(this.$contentWrapper, {
		xPercent: 100,
		opacity: 0,
		duration: 0.3,
		ease: 'power2.in'
	}, 0);
	
	// Animate overlay fade out
	this.timeline.to(this.$overlay, {
		opacity: 0,
		duration: 0.2,
		ease: 'power2.in'
	}, 0.1);

};

/**
 * Open animation for full type
 */
Modal.prototype.openFull = function() {

	var self = this;
	
	// Kill any existing timeline
	if (this.timeline) {
		this.timeline.kill();
	}
	
	this.timeline = gsap.timeline({
		onComplete: function() {
			self.timeline = null;
		}
	});
	
	// Animate overlay fade in
	this.timeline.to(this.$overlay, {
		opacity: 1,
		duration: 0.3,
		ease: 'power2.out'
	}, 0);
	
	// Animate content slide up from bottom (yPercent: 0 brings it to its CSS position)
	this.timeline.to(this.$contentWrapper, {
		yPercent: 0,
		opacity: 1,
		duration: 0.5,
		ease: 'power3.out'
	}, 0.1);

};

/**
 * Close animation for full type
 */
Modal.prototype.closeFull = function(onCompleteCallback) {

	var self = this;
	
	// Kill any existing timeline
	if (this.timeline) {
		this.timeline.kill();
	}
	
	this.timeline = gsap.timeline({
		onComplete: function() {
			self.options.$el.css('display', 'none');
			self.setInitialState();
			self.timeline = null;
			// Animation complete - callback is a no-op since we already called enableScrolling immediately
			if (onCompleteCallback) {
				onCompleteCallback();
			}
		}
	});
	
	// Animate content slide down
	this.timeline.to(this.$contentWrapper, {
		yPercent: 100,
		opacity: 0,
		duration: 0.3,
		ease: 'power2.in'
	}, 0);
	
	// Animate overlay fade out
	this.timeline.to(this.$overlay, {
		opacity: 0,
		duration: 0.2,
		ease: 'power2.in'
	}, 0.1);

};

/**
 * Open animation for center type
 */
Modal.prototype.openCenter = function() {

	var self = this;
	
	// Kill any existing timeline
	if (this.timeline) {
		this.timeline.kill();
	}
	
	this.timeline = gsap.timeline({
		onComplete: function() {
			self.timeline = null;
		}
	});
	
	// Animate overlay fade in
	this.timeline.to(this.$overlay, {
		opacity: 1,
		duration: 0.3,
		ease: 'power2.out'
	}, 0);
	
	// Animate content fade in and scale up
	// Maintain xPercent and yPercent for centering
	this.timeline.to(this.$contentWrapper, {
		opacity: 1,
		scale: 1,
		xPercent: -50,
		yPercent: -50,
		duration: 0.4,
		ease: 'back.out(1.2)'
	}, 0.1);

};

/**
 * Close animation for center type
 */
Modal.prototype.closeCenter = function(onCompleteCallback) {

	var self = this;
	
	// Kill any existing timeline
	if (this.timeline) {
		this.timeline.kill();
	}
	
	this.timeline = gsap.timeline({
		onComplete: function() {
			self.options.$el.css('display', 'none');
			self.setInitialState();
			self.timeline = null;
			// Animation complete - callback is a no-op since we already called enableScrolling immediately
			if (onCompleteCallback) {
				onCompleteCallback();
			}
		}
	});
	
	// Animate content fade out and scale down
	// Maintain xPercent and yPercent for centering
	this.timeline.to(this.$contentWrapper, {
		opacity: 0,
		scale: 0.8,
		xPercent: -50,
		yPercent: -50,
		duration: 0.3,
		ease: 'power2.in'
	}, 0);
	
	// Animate overlay fade out
	this.timeline.to(this.$overlay, {
		opacity: 0,
		duration: 0.2,
		ease: 'power2.in'
	}, 0.1);

};

/**
 * Check if modal is currently open
 */
Modal.prototype.getIsOpen = function() {
	return this.isOpen;
};

/**
 * Bind ESC key handler
 */
Modal.prototype.bindEscapeKey = function() {

	var self = this;
	
	// Use a single global ESC handler
	if (!window.ModalEscapeHandlerBound) {
		$(document).on('keydown.modal', function(e) {
			if (e.keyCode === 27) { // ESC key
				// Find and close the currently open modal
				if (window.ModalInstances) {
					Object.keys(window.ModalInstances).forEach(function(modalId) {
						var modal = window.ModalInstances[modalId];
						if (modal && modal.isOpen) {
							modal.close();
						}
					});
				}
			}
		});
		window.ModalEscapeHandlerBound = true;
	}

};

/**
 * Initialize global modal state management
 * (Currently not needed - Sitewide handles scrolling with reference counter)
 */
Modal.initGlobalStateManagement = function() {
	// No-op: Sitewide's reference counter system handles scrolling state
	// Each modal calls disableScrolling on open and enableScrolling on close
	// Sitewide only actually re-enables scrolling when counter reaches 0
	if (!window.ModalStateManagementInitialized) {
		window.ModalStateManagementInitialized = true;
	}
};

/**
 * Initialize trigger handlers for all modals
 * This should be called after all modals are initialized
 */
Modal.initTriggers = function() {

	// Listen for clicks on anchor links with href="#modal-id-XXX" pattern (default)
	// Use event delegation with higher priority to ensure it runs before Sitewide handlers
	$(document).on('click.modal', 'a[href^="#modal-"]', function(e) {
		e.preventDefault();
		e.stopPropagation();
		
		var href = $(this).attr('href');
		var modalId = href.substring(1); // Remove the #
		
		// Find the modal instance
		if (window.ModalInstances && window.ModalInstances[modalId]) {
			window.ModalInstances[modalId].open();
		} else {
			console.warn('Modal: No modal instance found for ID:', modalId);
		}
	});
	
	// Listen for clicks on anchor links with custom trigger patterns
	// This handles triggers defined in the CMS (modal_trigger field)
	$(document).on('click.modal-trigger', 'a[href^="#"]', function(e) {
		var href = $(this).attr('href');
		
		// Skip if it's already handled by the default pattern
		if (href.indexOf('#modal-') === 0) {
			return;
		}
		
		// Special handling for Apply Now buttons with unit-title
		if (href === '#apply-now') {
			e.preventDefault();
			e.stopPropagation();
			
			var $button = $(this);
			var unitTitle = $button.data('unit-title');
			
			// Helper function to update unit fields in forms
			var updateUnitFieldInAllForms = function(unitTitle, callback) {
				if (!unitTitle) {
					if (callback) callback();
					return;
				}
				
				const fieldHandles = ['unit', 'Unit', 'unitTitle', 'unit_title'];
				var fieldsUpdated = 0;
				
				fieldHandles.forEach(function(handle) {
					var $unitFields = $(`input[name="fields[${handle}]"], select[name="fields[${handle}]"], textarea[name="fields[${handle}]"]`);
					
					$unitFields.each(function() {
						var $field = $(this);
						if ($field.is('select')) {
							$field.val(unitTitle).trigger('change');
						} else {
							$field.val(unitTitle).trigger('input');
						}
						fieldsUpdated++;
					});
				});
				
				if (callback) {
					setTimeout(callback, 100);
				}
			};
			
			// If unit title is provided, update form fields first
			if (unitTitle) {
				updateUnitFieldInAllForms(unitTitle, function() {
					// Fields updated, now open the modal
					if (window.ModalTriggers && window.ModalTriggers[href]) {
						window.ModalTriggers[href].open();
					} else if (window.ModalTriggers && window.ModalTriggers['apply-now']) {
						window.ModalTriggers['apply-now'].open();
					}
				});
			} else {
				// No unit title, open modal immediately
				if (window.ModalTriggers && window.ModalTriggers[href]) {
					window.ModalTriggers[href].open();
				} else if (window.ModalTriggers && window.ModalTriggers['apply-now']) {
					window.ModalTriggers['apply-now'].open();
				}
			}
			return;
		}
		
		// Check if this href matches any custom trigger
		if (window.ModalTriggers && window.ModalTriggers[href]) {
			e.preventDefault();
			e.stopPropagation();
			window.ModalTriggers[href].open();
		} else if (window.ModalTriggers) {
			// Also check without the # prefix
			var triggerKey = href.substring(1);
			if (window.ModalTriggers[triggerKey]) {
				e.preventDefault();
				e.stopPropagation();
				window.ModalTriggers[triggerKey].open();
			}
		}
	});

};

export default Modal;
